import React, { useState } from 'react';
import { FaSearch, FaTimes, FaCar, FaFilter, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import Button from '@/components/common/Button';
import CustomSelect from '@/components/common/CustomSelect';
import VehiculoSelector from '@/components/common/VehiculoSelector';

/**
 * Componente de filtros avanzados para el marketplace de repuestos
 * Incluye búsqueda por vehículo, texto, categoría y otros filtros
 */
const FiltrosRepuestosMarketplace = ({
  onSearch,
  searchValue = '',
  onVehiculoChange,
  marcaVehiculo = '',
  modeloVehiculo = '',
  onCategoriaChange,
  categoriaSeleccionada = '',
  onTipoStockChange,
  tipoStockSeleccionado = 'todos',
  onOrdenarPorChange,
  ordenarPorSeleccionado = 'nombre',
  onReset,
  categorias = []
}) => {
  const [filtrosExpandidos, setFiltrosExpandidos] = useState(false);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (onSearch) {
      onSearch(value);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && onSearch) {
      onSearch(e.target.value);
    }
  };

  const handleMarcaChange = (e) => {
    if (onVehiculoChange) {
      onVehiculoChange({
        marca: e.target.value,
        modelo: '' // Limpiar modelo cuando cambia la marca
      });
    }
  };

  const handleModeloChange = (e) => {
    if (onVehiculoChange) {
      onVehiculoChange({
        marca: marcaVehiculo,
        modelo: e.target.value
      });
    }
  };

  const handleReset = () => {
    if (onReset) {
      onReset();
    }
  };

  const tiposStock = [
    { value: 'todos', label: 'Todos los productos' },
    { value: 'disponibles', label: 'Solo disponibles' },
    { value: 'agotados', label: 'Agotados' }
  ];

  const opcionesOrdenar = [
    { value: 'nombre', label: 'Nombre A-Z' },
    { value: 'precio', label: 'Precio: menor a mayor' },
    { value: 'stock', label: 'Stock: mayor a menor' },
    { value: 'descuento', label: 'Mayor descuento' }
  ];

  return (
    <div className="bg-gray-800 rounded-lg p-4 mb-6 border border-gray-700">
      {/* Búsqueda principal */}
      <div className="flex flex-col lg:flex-row gap-4 mb-4">
        {/* Campo de búsqueda de repuestos */}
        <div className="flex-1">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              className="w-full pl-10 pr-10 p-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
              placeholder="Buscar repuestos por nombre, código, marca..."
              value={searchValue}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
            />
            {searchValue && (
              <button
                onClick={() => onSearch('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <FaTimes className="text-gray-400 hover:text-white transition-colors" />
              </button>
            )}
          </div>
        </div>

        {/* Botón para expandir filtros */}
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => setFiltrosExpandidos(!filtrosExpandidos)}
            icon={filtrosExpandidos ? <FaChevronUp /> : <FaChevronDown />}
            className="whitespace-nowrap"
          >
            <FaFilter className="mr-2" />
            Filtros Avanzados
          </Button>
          
          <Button
            variant="ghost"
            onClick={handleReset}
            icon={<FaTimes />}
            className="whitespace-nowrap"
          >
            Limpiar
          </Button>
        </div>
      </div>

      {/* Filtros expandidos */}
      {filtrosExpandidos && (
        <div className="space-y-4 pt-4 border-t border-gray-700">
          {/* Selector de vehículo */}
          <div>
            <div className="flex items-center mb-3">
              <FaCar className="text-blue-400 mr-2" />
              <h4 className="text-white font-medium">Filtrar por Vehículo</h4>
            </div>
            <VehiculoSelector
              marcaValue={marcaVehiculo}
              modeloValue={modeloVehiculo}
              onMarcaChange={handleMarcaChange}
              onModeloChange={handleModeloChange}
              marcaName="marca_filtro"
              modeloName="modelo_filtro"
              required={false}
              allowManualEntry={true}
            />
            {(marcaVehiculo || modeloVehiculo) && (
              <div className="mt-2 text-sm text-blue-300">
                Filtrando repuestos compatibles con: {marcaVehiculo} {modeloVehiculo}
              </div>
            )}
          </div>

          {/* Otros filtros */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Categoría */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Categoría
              </label>
              <CustomSelect
                value={categoriaSeleccionada}
                onChange={(e) => onCategoriaChange && onCategoriaChange(e.target.value)}
                placeholder="Todas las categorías"
                options={[
                  { value: '', label: 'Todas las categorías' },
                  ...categorias.map(cat => ({
                    value: cat,
                    label: cat
                  }))
                ]}
                className="w-full"
              />
            </div>

            {/* Tipo de stock */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Disponibilidad
              </label>
              <CustomSelect
                value={tipoStockSeleccionado}
                onChange={(e) => onTipoStockChange && onTipoStockChange(e.target.value)}
                options={tiposStock}
                className="w-full"
              />
            </div>

            {/* Ordenar por */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Ordenar por
              </label>
              <CustomSelect
                value={ordenarPorSeleccionado}
                onChange={(e) => onOrdenarPorChange && onOrdenarPorChange(e.target.value)}
                options={opcionesOrdenar}
                className="w-full"
              />
            </div>
          </div>

          {/* Información de filtros activos */}
          <div className="flex flex-wrap gap-2 pt-2">
            {searchValue && (
              <span className="px-2 py-1 bg-blue-900/50 text-blue-200 text-xs rounded border border-blue-800">
                Búsqueda: "{searchValue}"
              </span>
            )}
            {marcaVehiculo && (
              <span className="px-2 py-1 bg-green-900/50 text-green-200 text-xs rounded border border-green-800">
                Marca: {marcaVehiculo}
              </span>
            )}
            {modeloVehiculo && (
              <span className="px-2 py-1 bg-green-900/50 text-green-200 text-xs rounded border border-green-800">
                Modelo: {modeloVehiculo}
              </span>
            )}
            {categoriaSeleccionada && (
              <span className="px-2 py-1 bg-purple-900/50 text-purple-200 text-xs rounded border border-purple-800">
                Categoría: {categoriaSeleccionada}
              </span>
            )}
            {tipoStockSeleccionado !== 'todos' && (
              <span className="px-2 py-1 bg-yellow-900/50 text-yellow-200 text-xs rounded border border-yellow-800">
                Stock: {tiposStock.find(t => t.value === tipoStockSeleccionado)?.label}
              </span>
            )}
            {ordenarPorSeleccionado !== 'nombre' && (
              <span className="px-2 py-1 bg-gray-700 text-gray-300 text-xs rounded border border-gray-600">
                Orden: {opcionesOrdenar.find(o => o.value === ordenarPorSeleccionado)?.label}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FiltrosRepuestosMarketplace;